import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Download,
  Filter,
  LineChart,
  RefreshCcw,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  reportsService,
  HotelPerformanceFilters,
  HotelPerformanceResponse,
} from "../../services/reportsService";
import hotelService from "../../services/hotelService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Chart } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

interface HotelOption {
  id: number;
  hotel_name: string;
  city?: string;
}

const QUICK_RANGES = [
  { id: "7d", label: "Last 7 Days", subtract: { days: 6 } },
  { id: "15d", label: "Last 15 Days", subtract: { days: 14 } },
  { id: "30d", label: "Last 30 Days", subtract: { days: 29 } },
  { id: "3m", label: "Last 3 Months", subtract: { months: 3 } },
  { id: "12m", label: "Last 12 Months", subtract: { months: 12 } },
];

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const subtractFromDate = (
  date: Date,
  offset: { days?: number; months?: number }
) => {
  const result = new Date(date);
  if (offset.months) {
    const month = result.getMonth();
    result.setMonth(month - offset.months);
    if (result > date) {
      result.setDate(1);
    }
  }
  if (offset.days) {
    result.setDate(result.getDate() - offset.days);
  }
  return result;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatNumber = (value: number, fractionDigits = 0) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value || 0);

const HotelPerformanceReport: React.FC = () => {
  const today = useMemo(() => new Date(), []);
  const defaultDateTo = useMemo(() => formatDate(today), [today]);
  const defaultDateFrom = useMemo(
    () => formatDate(subtractFromDate(today, { days: 29 })),
    [today]
  );

  const [filters, setFilters] = useState<HotelPerformanceFilters>({
    date_from: defaultDateFrom,
    date_to: defaultDateTo,
    group_by: "weekly",
  });

  const [selectedQuickRange, setSelectedQuickRange] = useState<string>("30d");

  const [reportData, setReportData] = useState<
    HotelPerformanceResponse["data"] | null
  >(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [hotels, setHotels] = useState<HotelOption[]>([]);

  useEffect(() => {
    const loadHotels = async () => {
      try {
        const response = await hotelService.getHotels(1, 1000);
        setHotels(response.data || []);
      } catch (err) {
        console.error("Error fetching hotels list:", err);
      }
    };

    loadHotels();
  }, []);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportsService.getHotelPerformanceReport(filters);
      setReportData(response.data);
    } catch (err) {
      console.error("Error fetching hotel performance report:", err);
      setError("Failed to load hotel performance report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickRange = (rangeId: string) => {
    const selectedRange = QUICK_RANGES.find((range) => range.id === rangeId);
    if (!selectedRange) return;

    const endDate = new Date();
    let startDate = subtractFromDate(endDate, selectedRange.subtract);
    if (startDate > endDate) {
      startDate = new Date(endDate);
    }

    setFilters((prev) => ({
      ...prev,
      date_from: formatDate(startDate),
      date_to: formatDate(endDate),
    }));
    setSelectedQuickRange(rangeId);
  };

  const handleFilterChange = (
    field: keyof HotelPerformanceFilters,
    value: string | number | undefined
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value === "" ? undefined : value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      date_from: defaultDateFrom,
      date_to: defaultDateTo,
      group_by: "weekly",
    });
    setSelectedQuickRange("30d");
  };

  const handleExport = () => {
    if (!reportData?.hotels?.length) {
      return;
    }

    const headers = [
      "Hotel",
      "City",
      "Total Bookings",
      "Fulfilled Bookings",
      "Total Revenue",
      "Room Nights",
      "ADR",
      "RevPAR",
      "Occupancy (%)",
      "Avg Length of Stay",
      "Cancellations",
      "No Shows",
    ];

    const rows = reportData.hotels.map((hotel) => [
      hotel.hotel_name,
      hotel.city ?? "",
      hotel.total_bookings,
      hotel.fulfilled_bookings,
      formatNumber(hotel.total_revenue, 2),
      formatNumber(hotel.room_nights, 2),
      formatNumber(hotel.average_daily_rate, 2),
      formatNumber(hotel.revenue_per_available_room, 2),
      formatNumber(hotel.occupancy_rate, 2),
      formatNumber(hotel.average_length_of_stay, 2),
      hotel.cancelled_bookings,
      hotel.no_show_bookings,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => {
            const stringValue = `${value ?? ""}`;
            if (
              stringValue.includes('"') ||
              stringValue.includes(",") ||
              stringValue.includes("\n")
            ) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const filename = `hotel_performance_${filters.date_from}_${filters.date_to}.csv`;
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const timeseriesLabels =
    reportData?.timeseries?.map(
      (item) => item.period_label || item.period_key
    ) || [];
  const revenueSeries =
    reportData?.timeseries?.map((item) => item.total_revenue || 0) || [];
  const bookingsSeries =
    reportData?.timeseries?.map((item) => item.total_bookings || 0) || [];
  const occupancySeries =
    reportData?.timeseries?.map((item) => item.occupancy_rate || 0) || [];

  const revenueVsBookingsData = {
    labels: timeseriesLabels,
    datasets: [
      {
        type: "line" as const,
        label: "Total Revenue (INR)",
        data: revenueSeries,
        borderColor: "#2563EB",
        backgroundColor: "rgba(37, 99, 235, 0.1)",
        tension: 0.3,
        yAxisID: "y1",
      },
      {
        type: "bar" as const,
        label: "Total Bookings",
        data: bookingsSeries,
        backgroundColor: "rgba(16, 185, 129, 0.4)",
        borderColor: "#10B981",
        borderWidth: 1,
        yAxisID: "y",
      },
    ],
  };

  const revenueVsBookingsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Bookings",
        },
      },
      y1: {
        beginAtZero: true,
        position: "right" as const,
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: "Revenue (INR)",
        },
      },
    },
  };

  const occupancyChartData = {
    labels: timeseriesLabels,
    datasets: [
      {
        label: "Occupancy (%)",
        data: occupancySeries,
        backgroundColor: "rgba(245, 158, 11, 0.5)",
        borderColor: "#F59E0B",
        borderWidth: 1,
      },
    ],
  };

  const occupancyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Occupancy (%)",
        },
      },
    },
  };

  const roomTypeData = {
    labels:
      reportData?.breakdowns?.room_types?.map((item) => item.room_type_name) ||
      [],
    datasets: [
      {
        label: "Room Nights",
        data:
          reportData?.breakdowns?.room_types?.map((item) => item.room_nights) ||
          [],
        backgroundColor: "rgba(99, 102, 241, 0.5)",
        borderColor: "#6366F1",
        borderWidth: 1,
      },
    ],
  };

  const occupancyHeatmapLabels =
    reportData?.breakdowns?.occupancy_by_weekday?.map(
      (item) => item.weekday_label
    ) || [];

  const occupancyHeatmapData = {
    labels: occupancyHeatmapLabels,
    datasets: [
      {
        label: "Occupancy (%)",
        data:
          reportData?.breakdowns?.occupancy_by_weekday?.map(
            (item) => item.occupancy_rate
          ) || [],
        backgroundColor: occupancyHeatmapLabels.map(() => "#3B82F6"),
        borderWidth: 0,
      },
    ],
  };

  const occupancyHeatmapOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Occupancy (%)",
        },
      },
      x: {
        title: {
          display: true,
          text: "Weekday",
        },
      },
    },
  };

  const leadTimeLabels =
    reportData?.breakdowns?.lead_time_distribution?.map(
      (item) => item.bucket
    ) || [];

  const leadTimeData = {
    labels: leadTimeLabels,
    datasets: [
      {
        label: "Bookings",
        data:
          reportData?.breakdowns?.lead_time_distribution?.map(
            (item) => item.total_bookings
          ) || [],
        backgroundColor: "#22C55E",
        borderRadius: 6,
      },
    ],
  };

  const leadTimeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Bookings",
        },
      },
      x: {
        title: {
          display: true,
          text: "Lead Time Bucket",
        },
      },
    },
  };

  const cityOptions = useMemo(() => {
    const uniqueCities = new Set<string>();
    hotels.forEach((hotel) => {
      if (hotel.city) {
        uniqueCities.add(hotel.city);
      }
    });
    return Array.from(uniqueCities).sort();
  }, [hotels]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <LineChart className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              Hotel Performance Report
            </h1>
          </div>
          <p className="text-gray-600 mt-2">
            Analyze hotel performance across flexible date ranges with detailed
            metrics and visualizations.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={resetFilters}
            className="flex items-center space-x-2"
          >
            <RefreshCcw className="w-4 h-4" />
            <span>Reset</span>
          </Button>
          <Button
            onClick={handleExport}
            disabled={!reportData?.hotels?.length}
            className="flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-gray-200 bg-white">
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="flex items-center space-x-3">
            <Filter className="w-5 h-5 text-gray-600" />
            <CardTitle className="text-lg font-semibold text-gray-900">
              Filters
            </CardTitle>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
            {QUICK_RANGES.map((range) => (
              <Button
                key={range.id}
                variant={
                  selectedQuickRange === range.id ? "default" : "outline"
                }
                size="sm"
                onClick={() => handleQuickRange(range.id)}
              >
                {range.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Hotel</label>
              <select
                value={filters.hotel_id ?? ""}
                onChange={(e) =>
                  handleFilterChange(
                    "hotel_id",
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Hotels</option>
                {hotels.map((hotel) => (
                  <option key={hotel.id} value={hotel.id}>
                    {hotel.hotel_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">City</label>
              <select
                value={filters.city ?? ""}
                onChange={(e) =>
                  handleFilterChange("city", e.target.value || undefined)
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Cities</option>
                {cityOptions.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Date From
              </label>
              <Input
                type="date"
                value={filters.date_from}
                max={filters.date_to}
                onChange={(e) =>
                  handleFilterChange("date_from", e.target.value)
                }
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Date To
              </label>
              <Input
                type="date"
                value={filters.date_to}
                min={filters.date_from}
                onChange={(e) => handleFilterChange("date_to", e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <Button
                variant={filters.group_by === "weekly" ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilterChange("group_by", "weekly")}
              >
                Weekly
              </Button>
              <Button
                variant={filters.group_by === "monthly" ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilterChange("group_by", "monthly")}
              >
                Monthly
              </Button>
              <Button
                variant={filters.group_by === "yearly" ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilterChange("group_by", "yearly")}
              >
                Yearly
              </Button>
            </div>

            <Button
              onClick={loadReport}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              <Calendar className="w-4 h-4" />
              <span>{loading ? "Loading..." : "Apply Filters"}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card className="border border-gray-200 bg-white">
          <CardContent className="py-16 text-center text-gray-500">
            Loading report data...
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border border-red-200 bg-white">
          <CardContent className="py-16 text-center text-red-500">
            {error}
          </CardContent>
        </Card>
      ) : reportData ? (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>Total Revenue</CardTitle>
                <CardDescription>
                  Confirmed, checked-in, and checked-out bookings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(reportData.summary.total_revenue)}
                </p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>Total Bookings</CardTitle>
                <CardDescription>Across selected filters</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900">
                  {formatNumber(reportData.summary.total_bookings)}
                </p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>ADR</CardTitle>
                <CardDescription>Average daily rate</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(reportData.summary.average_daily_rate)}
                </p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>RevPAR</CardTitle>
                <CardDescription>Revenue per available room</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(
                    reportData.summary.revenue_per_available_room
                  )}
                </p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>Occupancy</CardTitle>
                <CardDescription>
                  Total occupancy across selected period
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900">
                  {formatNumber(reportData.summary.occupancy_rate, 2)}%
                </p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>Cancellation Rate</CardTitle>
                <CardDescription>Cancelled bookings vs total</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900">
                  {formatNumber(reportData.summary.cancellation_rate, 2)}%
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>Revenue vs Bookings</CardTitle>
                <CardDescription>
                  Grouped by {reportData.group_by}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <Chart
                    type="bar"
                    data={revenueVsBookingsData}
                    options={revenueVsBookingsOptions}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>Occupancy Trend</CardTitle>
                <CardDescription>
                  Occupancy rate over selected period
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <Bar
                    data={occupancyChartData}
                    options={occupancyChartOptions}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>Occupancy Heatmap by Weekday</CardTitle>
                <CardDescription>
                  Average occupancy percentage across weekdays
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reportData?.breakdowns?.occupancy_by_weekday?.some(
                  (item) => item.rooms_capacity > 0
                ) ? (
                  <div className="h-64">
                    <Bar
                      data={occupancyHeatmapData}
                      options={occupancyHeatmapOptions}
                    />
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-sm text-gray-500">
                    No occupancy data available for the selected filters.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>Room Type Contribution</CardTitle>
                <CardDescription>Room nights by room type</CardDescription>
              </CardHeader>
              <CardContent>
                {reportData?.breakdowns?.room_types?.length ? (
                  <div className="h-64">
                    <Bar
                      data={roomTypeData}
                      options={{ responsive: true, maintainAspectRatio: false }}
                    />
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-sm text-gray-500">
                    No room type data available for the selected filters.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardHeader>
                <CardTitle>Booking Lead Time Distribution</CardTitle>
                <CardDescription>
                  How far in advance guests book their stays
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reportData?.breakdowns?.lead_time_distribution?.some(
                  (item) => item.total_bookings > 0
                ) ? (
                  <div className="h-64">
                    <Bar data={leadTimeData} options={leadTimeOptions} />
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-sm text-gray-500">
                    No lead time data available for the selected filters.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Data Table */}
          <Card className="border border-gray-200 bg-white">
            <CardHeader>
              <CardTitle>Hotel Performance Detail</CardTitle>
              <CardDescription>
                Tabular view of metrics per hotel
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {reportData.hotels.length === 0 ? (
                <div className="text-center text-gray-500 py-10">
                  No data available for the selected filters.
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Hotel
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        City
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Bookings
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Revenue
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Room Nights
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        ADR
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        RevPAR
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Occupancy
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Avg LOS
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Cancellations
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        No Shows
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportData.hotels.map((hotel) => (
                      <tr key={hotel.hotel_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {hotel.hotel_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {hotel.city ?? "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatNumber(hotel.total_bookings)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatCurrency(hotel.total_revenue)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatNumber(hotel.room_nights, 2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatCurrency(hotel.average_daily_rate)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatCurrency(hotel.revenue_per_available_room)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatNumber(hotel.occupancy_rate, 2)}%
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatNumber(hotel.average_length_of_stay, 2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatNumber(hotel.cancelled_bookings)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatNumber(hotel.no_show_bookings)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="border border-gray-200 bg-white">
          <CardContent className="py-16 text-center text-gray-500">
            Adjust the filters and click apply to load hotel performance data.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HotelPerformanceReport;
